import { DialogInfo, TableRowOption } from '@common/types/common';
import { PaginationReturn } from '@common/types/service';
import { InviteCompanyDialog } from '@company/components/display/dialogs/InviteCompanyDialog';
import { CompanyManagementContext } from '@company/contexts/CompanyManagementContext';
import {
    useAnswerPartnershipRequestMutation,
    useDeleteMultiplePartnershipsMutation,
    useDeletePartnershipMutation,
    useGetCompanyPartnershipsQuery,
} from '@company/services/company';
import { IPartnership, StatusRequest } from '@company/types/company';
import { PartnershipColumns, PartnershipManagementHeader } from '@company/types/partnership';
import { SERVICES_ICONS } from '@company/utils/company';
import { PARTNERSHIP_MANAGEMENT_COLUMNS } from '@company/utils/partnership';
import { CancelDeleteDialog } from '@components/display/dialogs/CancelDeleteDialog';
import { LoadingContainer } from '@components/display/loading/LoadingContainer';
import { Table } from '@components/display/tables';
import { Filters } from '@components/utility/filters';
import { useDebounce } from '@hooks/useDebounce';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { CircularProgress, Tooltip } from '@mui/material';
import Button from '@mui/material/Button/Button';
import Menu from '@mui/material/Menu/Menu';
import MenuItem from '@mui/material/MenuItem/MenuItem';
import TableCell from '@mui/material/TableCell/TableCell';
import Typography from '@mui/material/Typography/Typography';
import { capitalizeFirstLetter, handleError, toaster } from '@utils/common';
import { createElement, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const allowedColumns: PartnershipColumns[] = ['company_name', 'country__name'];

const PartnershipManagement = () => {
    const { permissions, company } = useContext(CompanyManagementContext);
    const [searchParams, setSearchParams] = useSearchParams();

    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [isInviteOpen, setIsInviteOpen] = useState(false);
    const [actionsAnchorEl, setActionsAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedItems, setSelectedItems] = useState<number[]>([]);
    const [dialogInfo, setDialogInfo] = useState<DialogInfo | null>(null);

    const filters = useMemo(
        () => ({
            sort: allowedColumns.includes(searchParams.get('sort')?.replace('-', '') as PartnershipColumns)
                ? (searchParams.get('sort') as PartnershipColumns)
                : 'name',
            page: Number.isNaN(searchParams.get('page')) || !searchParams.get('page') ? '1' : searchParams.get('page'),
            search: searchParams.get('search'),
        }),
        [searchParams]
    );
    const sort: { column: PartnershipColumns; direction: 'asc' | 'desc' } = useMemo(
        () => ({
            column: filters.sort.replace('-', '') as PartnershipColumns,
            direction: filters.sort.includes('-') ? 'desc' : 'asc',
        }),
        [filters.sort]
    );

    const { data: partnershipsResponse, isFetching: isPartnershipsFetching } = useGetCompanyPartnershipsQuery({
        company_id: company?.id ?? '',
        ordering: filters.sort,
        page: filters.page ? Number(filters.page) : 1,
        page_size: 20,
        filters: {
            search: filters.search ? [filters.search] : [],
        },
    });
    const partnerships = partnershipsResponse as PaginationReturn<IPartnership> | undefined;

    const [deletePartnership, { isLoading: isDeletePartnershipLoading }] = useDeletePartnershipMutation();
    const [deleteMultiplePartnerships, { isLoading: isDeleteMultiplePartnershipsLoading }] =
        useDeleteMultiplePartnershipsMutation();
    const [answerPartnershipRequest, { isLoading: isAnswerPartnershipRequestLoading }] =
        useAnswerPartnershipRequestMutation();

    const [search, setSearch] = useState(filters.search);
    const resetFilters = useCallback(() => {
        setSearchParams((prev) => {
            const params = new URLSearchParams(prev);
            params.set('page', '1');
            params.delete('role');
            return params;
        });
    }, [setSearchParams]);

    const handleParamChange = useCallback(
        (field: string, value: string | null) => {
            if (searchParams.get(field) !== value) {
                setSearchParams((prev) => {
                    const params = new URLSearchParams(prev);
                    params.set('page', '1');
                    if (value) {
                        params.set(field, value);
                    } else {
                        params.delete(field);
                    }
                    return params;
                });
            }
        },
        [searchParams, setSearchParams]
    );

    const handleChipLabel = useCallback((field: string, value: string) => {
        return value;
    }, []);

    const setDialog = useCallback((title: string, primaryLabel: string, primaryOnClick: () => void) => {
        setDialogInfo({
            title,
            primary: {
                label: primaryLabel,
                onClick: primaryOnClick,
            },
            secondary: {
                label: 'Go back',
                onClick: () => setDialogInfo(null),
            },
        });
    }, []);

    const handleDeleteMultiplePartnerships = useCallback(async () => {
        try {
            if (company?.id && selectedItems.length) {
                await deleteMultiplePartnerships({ company_id: company.id, partnership_ids: selectedItems }).unwrap();
                toaster.success({ msg: 'Successfully removed partnership ' });
                setDialogInfo(null);
                setSelectedItems([]);
            }
        } catch (err) {
            handleError('Error deleting partnerships', err);
        }
    }, [company?.id, deleteMultiplePartnerships, selectedItems]);

    const rightFilterComponent = useMemo(
        () => (
            <div className="xs:ml-auto flex gap-2 flex-col 2md:flex-row">
                <Button
                    variant="outlined"
                    color="inherit"
                    onClick={(e) => setActionsAnchorEl(e.currentTarget)}
                    endIcon={<KeyboardArrowDownIcon />}
                    className="!bg-anti-flash-white !border-none"
                    disabled={selectedItems.length === 0}
                >
                    Actions
                </Button>
                <Menu
                    anchorEl={actionsAnchorEl}
                    open={Boolean(actionsAnchorEl)}
                    onClose={() => setActionsAnchorEl(null)}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                    }}
                >
                    <MenuItem
                        onClick={() => {
                            setDialog(
                                'Are you sure you want to remove these partners?',
                                'Remove',
                                handleDeleteMultiplePartnerships
                            );
                            setActionsAnchorEl(null);
                        }}
                        sx={{ minWidth: 150, justifyContent: 'center' }}
                        disabled={selectedItems.length === 0}
                    >
                        <Typography color={'error'}>Remove ({selectedItems.length})</Typography>
                    </MenuItem>
                </Menu>
                <Button onClick={() => setIsInviteOpen(true)}>Invite companies</Button>
            </div>
        ),
        [actionsAnchorEl, handleDeleteMultiplePartnerships, selectedItems.length, setDialog]
    );

    const handleSortClick = useCallback(
        (column: keyof (PartnershipManagementHeader & { country__name: string; company_name: string })) => {
            const isAsc = sort.column === column && sort.direction === 'asc';
            setSelectedItems([]);
            setSearchParams((prev) => {
                const params = new URLSearchParams(prev);
                params.set('sort', `${isAsc ? '-' : ''}${column}`);
                params.set('page', '1');
                return params;
            });
        },
        [setSearchParams, sort.column, sort.direction]
    );

    const handleSelected = useCallback((row: IPartnership) => {
        const row_ = row.id;
        setSelectedItems((prevSelectedItems) => {
            const exists = prevSelectedItems.indexOf(row_) !== -1;
            return exists ? prevSelectedItems.filter((x) => x !== row_) : [...prevSelectedItems, row_];
        });
    }, []);

    const handleSelectAll = useCallback(() => {
        const allItems = partnerships?.results.map((x) => x.id);
        selectedItems.length === allItems?.length ? setSelectedItems([]) : setSelectedItems(allItems || []);
    }, [partnerships?.results, selectedItems.length]);

    const handlePageChange = useCallback(
        (newPage: number) => {
            setSearchParams((prev) => {
                const params = new URLSearchParams(prev);
                params.set('page', `${newPage}`);
                return params;
            });
            selectedItems.length && setSelectedItems([]);
        },
        [selectedItems.length, setSearchParams]
    );

    const handleDeletePartnership = useCallback(
        async (id?: number, other_company_id?: string) => {
            try {
                if (company?.id && id) {
                    await deletePartnership({
                        company_id: company?.id,
                        partnership_id: id,
                        other_company_id: other_company_id ?? '',
                    }).unwrap();
                    toaster.success({ msg: 'Successfully removed partnership ' });
                    setDialogInfo(null);
                }
            } catch (err) {
                handleError('Error deleting partnership', err);
            }
        },
        [company?.id, deletePartnership]
    );

    const rowOptions: TableRowOption<IPartnership>[] | undefined = useMemo(() => {
        return permissions?.partnerships.manage
            ? [
                  {
                      label: 'Remove',
                      color: 'error',
                      onClick: (partnership) => {
                          setDialog('Are you sure you want to delete this partnership?', 'Remove', () =>
                              handleDeletePartnership(partnership.id, partnership.companies?.[0].id)
                          );
                      },
                  },
              ]
            : [];
    }, [handleDeletePartnership, permissions?.partnerships.manage, setDialog]);

    const handleAnswerPartnershipRequest = useCallback(
        async (answer: boolean, request_id?: number, to_company_id?: string) => {
            try {
                if (company?.id && request_id) {
                    await answerPartnershipRequest({
                        company_id: company.id,
                        request_id,
                        accept: answer,
                        to_company_id: to_company_id ?? '',
                    }).unwrap();
                    toaster.success({ msg: `Partnership request successfully ${answer ? 'accepted' : 'declined'}` });
                }
            } catch (err) {
                handleError(`Error ${answer ? 'accepting' : 'declining'} partnership request`, err);
            }
        },
        [answerPartnershipRequest, company?.id]
    );

    const getStatus = useCallback(
        (partnership: Partial<IPartnership>) => {
            if (partnership.active) {
                return '';
            }
            const myRequest = partnership.my_request?.[0];
            const otherRequest = partnership.others_requests?.[0];
            if (myRequest) {
                if (myRequest.status === StatusRequest.Pending) {
                    return (
                        <div className="flex items-center gap-1">
                            {isAnswerPartnershipRequestLoading ? <CircularProgress size={20} /> : null}
                            <Button
                                variant="text"
                                color="success"
                                onClick={() => handleAnswerPartnershipRequest(true, myRequest.id, myRequest.company.id)}
                                disabled={isAnswerPartnershipRequestLoading}
                            >
                                Accept
                            </Button>
                            |
                            <Button
                                variant="text"
                                color="error"
                                onClick={() =>
                                    handleAnswerPartnershipRequest(false, myRequest.id, myRequest.company.id)
                                }
                                disabled={isAnswerPartnershipRequestLoading}
                            >
                                Decline
                            </Button>
                        </div>
                    );
                } else return myRequest.status;
            } else if (otherRequest) {
                switch (otherRequest.status) {
                    case StatusRequest.Pending:
                        return 'Pending outgoing request';
                    default:
                        return otherRequest.status;
                }
            }

            return 'Unknown';
        },
        [handleAnswerPartnershipRequest, isAnswerPartnershipRequestLoading]
    );

    const renderRow = useCallback(
        (partnership: Partial<IPartnership>) => {
            const data = partnership.companies?.[0];
            return (
                <>
                    <TableCell className="ml-10 w-72">{data?.name}</TableCell>
                    <TableCell>
                        <div className="flex gap-2">
                            {data?.services.map((service) => (
                                <Tooltip title={capitalizeFirstLetter(service.name.split('_').join(' '))}>
                                    <div>{createElement(SERVICES_ICONS[service.name].icon)}</div>
                                </Tooltip>
                            ))}
                        </div>
                    </TableCell>
                    <TableCell>{data?.country.name}</TableCell>
                    {permissions?.partnerships.manage ? <TableCell>{getStatus(partnership)}</TableCell> : null}
                </>
            );
        },
        [getStatus, permissions?.partnerships.manage]
    );

    const headers = useMemo(
        () =>
            permissions?.partnerships.manage
                ? PARTNERSHIP_MANAGEMENT_COLUMNS
                : PARTNERSHIP_MANAGEMENT_COLUMNS.slice(0, PARTNERSHIP_MANAGEMENT_COLUMNS.length - 1),
        [permissions?.partnerships.manage]
    );

    const handleSearchParamChange = useCallback(
        (value: string | null) => {
            handleParamChange('search', value);
        },
        [handleParamChange]
    );

    useDebounce({
        value: search,
        delay: 500,
        customHandler: handleSearchParamChange,
        trigger: () => !!permissions?.filter,
    });
    useEffect(() => {
        if (permissions?.filter) {
            setSearch(filters.search);
        }
    }, [filters.search, permissions?.filter]);

    return (
        <LoadingContainer isLoading={false}>
            {permissions?.filter ? (
                <Filters
                    searchInput={search ?? ''}
                    onSearchChange={(e) => setSearch(e.target.value)}
                    onSearchClear={() => setSearch('')}
                    searchPlaceholder="Search for partner..."
                    isOpen={isFilterOpen}
                    onFilterOpenToggle={() => setIsFilterOpen(!isFilterOpen)}
                    onClearFilterClick={resetFilters}
                    activeFilters={{}}
                    onChipClick={(field) => handleParamChange(field, '')}
                    getChipLabel={handleChipLabel}
                    right={permissions?.partnerships.manage ? rightFilterComponent : null}
                />
            ) : null}
            <Table
                headers={headers}
                data={partnerships?.results ?? []}
                renderRow={renderRow}
                selectable={permissions?.partnerships.manage}
                isRowSelectable={() => !!permissions?.partnerships.manage}
                emptyListTxt="There are currently no partnerships to list"
                rowsPerPage={20}
                selectedItems={selectedItems}
                onSortClick={handleSortClick}
                onRowCheckClick={handleSelected}
                onSelectAll={handleSelectAll}
                page={Number(filters.page)}
                onPageChange={handlePageChange}
                sort={sort}
                count={partnerships?.count || 0}
                rowOptions={{ data: rowOptions }}
                isLoading={isPartnershipsFetching}
                fillMissingRows
            />
            <CancelDeleteDialog
                open={!!dialogInfo}
                title={dialogInfo?.title}
                primary={{
                    label: dialogInfo?.primary.label || '',
                    onClick: dialogInfo?.primary.onClick || (() => {}),
                    loading: isDeletePartnershipLoading || isDeleteMultiplePartnershipsLoading,
                }}
                secondary={{
                    label: dialogInfo?.secondary.label || '',
                    onClick: () => setDialogInfo(null),
                }}
            />
            <InviteCompanyDialog
                open={isInviteOpen}
                onClose={() => setIsInviteOpen(false)}
                PaperProps={{ className: 'p-5' }}
            />
        </LoadingContainer>
    );
};

export { PartnershipManagement };
